import { Injectable } from '@angular/core';
import type { AccountInfo, PublicClientApplication } from '@azure/msal-browser';
import { environment } from 'src/environments/environment';
import { AuthService } from '../services/auth.service';

/**
 * The portal puts this on the address when it sends over someone already signed in there, so this
 * app can go straight into the group sign-in instead of showing its login form first. It says only
 * "came from the portal" — nothing about who. Contract: login-farmacias spec `sso-handoff`.
 */
const HANDOFF_PARAM = 'lf_handoff';

/** Set when this tab has asked login-farmacias, without a screen, whether its session is still open, so it asks once. */
const SESSION_TRIED_KEY = 'lf.session-tried';
/** The address that quiet check started from, to go back to afterwards. */
const SESSION_FROM_KEY = 'lf.session-from';

/**
 * login-farmacias sign-in, on `@azure/msal-browser` directly rather than `msal-angular` — this
 * app is on Angular 21 and bidscircleops on 22, which straddle msal-angular's supported range.
 *
 * MSAL is imported dynamically, so it stays out of the initial bundle and is fetched only when
 * this session has something to do with SSO. During the dual-run a legacy session never
 * downloads it at all.
 *
 * The MSAL cache is `sessionStorage`: a token lives as long as the tab. The legacy `currentUser`
 * object stays in localStorage for the legacy path.
 */
@Injectable({
  providedIn: 'root',
})
export class MsalService {
  private pca: PublicClientApplication | null = null;
  private account: AccountInfo | null = null;
  /** The quiet check (signInFromSession) came back with no session at login-farmacias: signed out, not an error. */
  public quietCheckFoundNoSession = false;

  constructor(private authService: AuthService) {}

  /**
   * Completes a redirect if this load is one, and otherwise picks up an account already in the
   * cache. Runs at bootstrap, before the router decides anything, so a returning user is signed
   * in by the time the first guard asks.
   */
  public async init(): Promise<void> {
    if (!this.mayNeedMsal()) {
      return;
    }

    const pca = await this.client();
    // Completes a redirect and, either way, clears the interaction flag MSAL sets when one
    // starts. A flag left behind makes every later loginRedirect throw interaction_in_progress.
    //
    // navigateToLoginRequestUrl defaults to true, which sends the browser from the callback
    // back to the page that started the login and leaves the response to be picked up there.
    // That page is the login form, which this app does not treat as a callback: the response
    // went unredeemed and the user landed back on the form with an interaction still open.
    // The callback route finishes the sign-in and routes onwards itself instead.
    let result: Awaited<ReturnType<PublicClientApplication['handleRedirectPromise']>> = null;
    try {
      result = await pca.handleRedirectPromise({ navigateToLoginRequestUrl: false });
    } catch (error) {
      const { InteractionRequiredAuthError } = await import('@azure/msal-browser');
      if (!(error instanceof InteractionRequiredAuthError && sessionTried())) {
        throw error;
      }
      this.quietCheckFoundNoSession = true;
    }

    // A cached account is restored only for a tab the app still considers signed in. MSAL's
    // cache outlives our marker, so after a guard has refused the session, adopting from it
    // would sign the user back in on each reload only to be refused again.
    this.account =
      result?.account ?? (this.authService.isSso ? pca.getAllAccounts()[0] ?? null : null);
    if (this.account) {
      this.authService.adoptSsoToken(await this.token());
    } else if (!(await this.startHandoff())) {
      await this.signInFromSession();
    }
  }

  /**
   * Someone signed in at login-farmacias earlier (another tab, another application, or before the browser closed when
   * they answered "Stay signed in") opens Bidscircle with no session here: MSAL's cache is per tab. Once per tab, the
   * page asks login-farmacias with `prompt=none` and comes back signed in without a screen; with no session there the
   * answer is `login_required` and the login page shows "Entrar" as before. Not with a legacy session, in a frame, on
   * the callback, or on a counter tablet (`lf_kiosk=1`), which keeps the previous person's session.
   */
  private async signInFromSession(): Promise<void> {
    if (!this.maySignInFromSession()) {
      return;
    }
    try {
      const from = window.location.pathname + window.location.search;
      sessionStorage.setItem(SESSION_TRIED_KEY, '1');
      sessionStorage.setItem(SESSION_FROM_KEY, from);
      const pca = await this.client();
      await pca.loginRedirect({
        scopes: [environment.sso.apiScope],
        extraQueryParameters: { brand: 'bidscircle' },
        prompt: 'none',
      });
    } catch (error) {
      console.warn('[SSO] the quiet sign-in did not start', error);
    }
  }

  private maySignInFromSession(): boolean {
    return (
      !!environment.sso?.clientId &&
      !this.authService.currentUserValue &&
      !sessionTried() &&
      !this.inFrame() &&
      window.location.pathname !== environment.sso.redirectPath &&
      new URLSearchParams(window.location.search).get('lf_kiosk') !== '1'
    );
  }

  /** A frame (an embedding, a test runner) never leaves for login-farmacias on its own. */
  protected inFrame(): boolean {
    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  }

  /** Where the quiet check started, once; null when there was none or it was the login page. */
  public takeSessionFrom(): string | null {
    try {
      const from = sessionStorage.getItem(SESSION_FROM_KEY);
      sessionStorage.removeItem(SESSION_FROM_KEY);
      return from && !from.startsWith('/account') ? from : null;
    } catch {
      return null;
    }
  }

  /**
   * Someone sent here from the portal is already signed in there, so asking them to press a button
   * to say so is a step with nothing in it. When the address carries the handoff marker and there is
   * no session on this side, go straight into the sign-in — which the live Entra session completes
   * without a prompt.
   *
   * The marker comes off the address *before* the redirect, not after: that is what stops a refused
   * or abandoned sign-in from starting another one when the person lands back here or reloads. It is
   * consumed once, by being consumed from history.
   */
  private async startHandoff(): Promise<boolean> {
    if (this.authService.isSso) {
      return false;
    }

    const url = new URL(window.location.href);
    if (!url.searchParams.has(HANDOFF_PARAM)) {
      return false;
    }

    url.searchParams.delete(HANDOFF_PARAM);
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);

    await this.login();
    return true;
  }

  public async login(): Promise<void> {
    const pca = await this.client();
    return pca.loginRedirect({
      scopes: [environment.sso.apiScope],
      extraQueryParameters: { brand: 'bidscircle' },
    });
  }

  /** A token good for the API, refreshed silently; falls back to a redirect when it cannot be. */
  public async token(): Promise<string> {
    if (!this.account) {
      throw new Error('no SSO account');
    }

    const pca = await this.client();
    const request = { scopes: [environment.sso.apiScope], account: this.account };

    try {
      return (await pca.acquireTokenSilent(request)).accessToken;
    } catch (error) {
      const { InteractionRequiredAuthError } = await import('@azure/msal-browser');
      if (error instanceof InteractionRequiredAuthError) {
        await pca.acquireTokenRedirect(request);
      }
      throw error;
    }
  }

  public isSso(): boolean {
    return this.account !== null;
  }

  /**
   * Ends the group session: here first, then at the portal, which ends it at the identity provider.
   *
   * Ending the Entra session from here would strand the portal — its cache is on another origin and
   * nothing would tell it the session had gone, so it would go on offering the applications, and the
   * next sign-out there would show an account picker with nothing left in it. Handing the sign-out to
   * the portal puts it in one place and one order (login-farmacias spec `sso-logout`).
   *
   * The legacy session is untouched: it never involved the portal.
   */
  public async logout(): Promise<void> {
    this.authService.logout();
    const pca = await this.client();

    const portal = environment.sso.portalLogoutUri;
    if (!portal) {
      // No portal configured: end it here rather than leave the person signed in.
      await pca.logoutRedirect();
      return;
    }

    // This origin's cache goes before we leave, because the portal cannot reach it.
    try {
      await pca.clearCache();
    } catch {
      /* the cache is going anyway; the portal still ends the session at Entra */
    }
    window.location.assign(portal);
  }

  /**
   * Whether loading MSAL could do anything at all — it stays out of the initial bundle, so this
   * is the decision to fetch it. True when this tab has adopted an SSO token, when the browser
   * is on the redirect URI, when the URL carries an authorization response, or when a previous
   * interaction left its flag behind and only MSAL can clear it.
   */
  private mayNeedMsal(): boolean {
    return (
      this.authService.isSso ||
      window.location.pathname === environment.sso.redirectPath ||
      /(^|[#&?])(code|error|state|id_token)=/.test(
        window.location.hash + window.location.search
      ) ||
      // A handover from the portal is precisely the case where there is no session yet, so it has
      // to be its own reason to fetch MSAL — otherwise the gate closes on the one person it is for.
      new URLSearchParams(window.location.search).has(HANDOFF_PARAM) ||
      hasPendingInteraction() ||
      // A tab with no session at all may still be signed in at login-farmacias (signInFromSession).
      this.maySignInFromSession()
    );
  }

  private async client(): Promise<PublicClientApplication> {
    if (!this.pca) {
      const { PublicClientApplication } = await import('@azure/msal-browser');
      this.pca = new PublicClientApplication({
        auth: {
          clientId: environment.sso.clientId,
          authority: environment.sso.authority,
          knownAuthorities: [environment.sso.authorityHost],
          // Built from the origin the app is actually served from, so one build works on
          // localhost and on the deployed app. Both origins are registered on the app.
          redirectUri: `${window.location.origin}${environment.sso.redirectPath}`,
        },
        cache: { cacheLocation: 'sessionStorage' },
      });
      await this.pca.initialize();
    }
    return this.pca;
  }
}

/**
 * Whether MSAL has an interaction marked as in progress. Its key is scoped by client id, so the
 * suffix is what identifies it. Storage access itself can throw (a browser set to block site
 * data), and a failure here must not stop the app from booting on the legacy path.
 */
function sessionTried(): boolean {
  try {
    return sessionStorage.getItem(SESSION_TRIED_KEY) === '1';
  } catch {
    return true;
  }
}

function hasPendingInteraction(): boolean {
  try {
    return Object.keys(sessionStorage).some((key) => key.endsWith('interaction.status'));
  } catch {
    return false;
  }
}
